import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Box,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExpandAllButton,
  ExportMenuItems,
  columnTypes,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  usePersistedBlotterTable,
  useSecuritiesContext,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { getCounterpartyMonitoringJsonObject, type CounterpartyMonitoringRow } from './counterpartyMonitoringRows';
import { useCounterpartyMonitoringColumns } from './useCounterpartyMonitoringColumns';
import { useCounterpartyMonitoringFilter } from './useCounterpartyMonitoringFilter';
import { useCounterpartyMonitoringObs } from './useCounterpartyMonitoringObs';

interface CounterpartyMonitoringBlotterTableProps {
  blotterID: string;
  tabLabel: string | undefined;
}

export function CounterpartyMonitoringBlotterTable({ blotterID, tabLabel }: CounterpartyMonitoringBlotterTableProps) {
  const persistedBlotterTable = usePersistedBlotterTable(blotterID);
  const columns = useCounterpartyMonitoringColumns();
  const { counterpartyMonitoringRowsObservable, errorObservable } = useCounterpartyMonitoringObs();
  const { securitiesBySymbol } = useSecuritiesContext();

  const { clientSideFilter, filterBuilderProps } = useCounterpartyMonitoringFilter({
    persistedBlotterTable,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps,
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        width: 420,
        hide: false,
        initialPinned: 'left',
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const blotterTable = useBlotterTable<CounterpartyMonitoringRow>({
    dataObservable: counterpartyMonitoringRowsObservable,
    errorObservable,
    showPinnedRows: false,
    rowID: 'rowID',
    clientLocalFilter: clientSideFilter,
    persistence: persistedBlotterTable,
    columns,
    contextMenuOptions: {
      dataDisplayCallback: row => getCounterpartyMonitoringJsonObject(row, securitiesBySymbol),
    },
    gridOptions: {
      groupDefaultExpanded: -1,
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
      getRowHeight: params => (params.node.group ? undefined : 52),
    },
    exportOptions: {
      name: 'Counterparty Monitoring',
      tabLabel,
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        showDateRangePicker={false}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
    </Box>
  );
}
